import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import adminService, { type InvestorLeadRow } from '../../../services/adminService';

const STATUSES = ['NEW', 'CONTACTED', 'CLOSED'] as const;

export default function AdminInvestorLeadsPage() {
  const [rows, setRows] = useState<InvestorLeadRow[]>([]);
  const [total, setTotal] = useState(0);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminService.getInvestorLeads({ role_filter: roleFilter || undefined, status_filter: statusFilter || undefined, limit: 50 })
      .then((res) => { setRows(res.leads); setTotal(res.total); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [roleFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id: string, status: string) => {
    await adminService.updateInvestorLead(id, { status });
    setRows((r) => r.map((x) => (x.id === id ? { ...x, status: status as InvestorLeadRow['status'] } : x)));
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="mx-auto max-w-5xl">
        <Link to="/admin/developers" className="text-sm text-[#003930] hover:underline">← Developers</Link>
        <h1 className="mb-4 text-2xl font-bold text-[var(--pv-color-primary)]">Investor leads <span className="text-base font-normal text-gray-400">({total})</span></h1>

        <div className="mb-4 flex flex-wrap gap-3">
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            <option value="">All roles</option>
            <option value="DEVELOPER">Developers</option>
            <option value="INVESTOR">Investors</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {loading ? (
            <p className="p-8 text-center text-sm text-gray-400">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-400">No leads.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Project / budget</th>
                  <th className="px-4 py-3">Developer</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{l.name}</div>
                      <div className="text-xs text-gray-400" dir="ltr">{l.email}{l.phone ? ` · ${l.phone}` : ''}</div>
                      {l.message && <div className="mt-1 max-w-xs text-xs text-gray-500">{l.message}</div>}
                    </td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] ${l.role === 'DEVELOPER' ? 'bg-amber-100 text-amber-700' : 'bg-[#DDEEEB] text-[#003930]'}`}>{l.role}</span></td>
                    <td className="px-4 py-3 text-gray-600">{l.projectOrBudget || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{l.company ? <a href={`/developers/${l.company.slug}`} target="_blank" rel="noreferrer" className="text-[#003930] hover:underline">{l.company.name}</a> : '-'}</td>
                    <td className="px-4 py-3">
                      <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className="rounded-lg border border-gray-300 px-2 py-1 text-xs">
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
